import { useCreativeAnalyticsContext } from "@/context-state/CreativeAnalyticsContext.jsx"
import { useDashboardContext } from "@/context-state/DashboardContext.jsx"
import { useMetaConnection } from "@/hooks/useMetaConnection.jsx"
import { useRelevantIntegration } from "@/hooks/useRelevantIntegration.jsx"
import { extractAllAdSetsFromAdReport, extractAllCampaignsFromAdReport, extractAllCreativesIdsFromAdReport, extractNamingConventionValues, filterAdsData, generateAdGroupsReports, generateRawAdsBenchmarkReport, generateRawAdsBenchmarkReportByDate, generateAssetBreakdownReport, generateNamingConventionReport } from "@/services/ads.jsx"
import { generateDateKey } from "@/utils/date.js"
import { NAMING_CONVENTION_GROUP_KEY, groupByCacheKey } from '@/services/namingConvention.js'
import { toast } from "sonner"
import * as API from "@/services/api.js"
import { handleError } from "@/services/errorHandler.js"
import { useSpaceAttributionWindow } from "@/hooks/useSpaceAttributionWindow.jsx"
// Ad spend cap banner is disabled for now (see below). Re-add this import if re-enabled.
// import { toggleAppBanner } from "@/hooks/useAppBanner"
import { useCurrentSpace } from "@/hooks/useCurrentSpace"
import syncCreativeDataToUse from "@/demo-data/demo-creative-data-to-use.json"
import syncAdsDataToUse from "@/demo-data/demo-data-to-use.json"

// Module-level concurrency limiter for ad-report fetches. Caps the number of
// in-flight account fetches so that selecting many ad accounts at once
// (CreativePage extras, views with multi-account widgets) doesn't fan out into
// a request storm against the backend / providers.
const MAX_CONCURRENT_FETCHES = 5
let inFlightCount = 0
const waitingResolvers = []
const acquireFetchSlot = () => new Promise((resolve) => {
    if (inFlightCount < MAX_CONCURRENT_FETCHES) {
        inFlightCount += 1
        resolve()
        return
    }
    waitingResolvers.push(resolve)
})
const releaseFetchSlot = () => {
    const next = waitingResolvers.shift()
    if (next) {
        // Hand the slot directly to the next waiter — keeps inFlightCount stable.
        next()
        return
    }
    inFlightCount = Math.max(0, inFlightCount - 1)
}

export const useAdsData = (setIsFetchingData) => {

    const { setMetaConnectionAsInActive } = useMetaConnection()
    const { adAccountsContext, creativeContext } = useDashboardContext()
    const { updateCreativeState, getCreativeFromState } = creativeContext
    const { space, isDemoSpace } = useCurrentSpace()
    const { chosenAdAccount } = adAccountsContext
    const { getAdsByKey, updateAdsByKey, triggerAnalyzeProcessPolling, updateNamingConventionValuesBySpaceId, updateCampaignsRelevantToAdsBySpaceId, updateAdSetsRelevantToAdsBySpaceId, isAdsKeyInFlight, getAdsKeyInFlightPromise, markAdsKeyInFlight, unmarkAdsKeyInFlight, setAdsKeyError, clearAdsKeyError } = useCreativeAnalyticsContext()
    const { updateReportByGroupType, updateReportGroupSlot } = useCreativeAnalyticsContext()
    const { integration: relevantIntegration, provider: integrationProvider, selectedAdAccount } = useRelevantIntegration()
    const { integrationsContext } = useDashboardContext()
    const allIntegrations = integrationsContext?.integrations || []

    const { attributionWindowKeys } = useSpaceAttributionWindow()
    const namingConvention = space.namingConvention
    const internalCustomMetrics = space.internalCustomMetrics
    /* This is used to fetch the ad reports data from meta api */
    // Axon supports two optional params: 'range' for asset reports (yesterday, last_7d, last_month) and 'breakdowns' for creative_set reports
    const fetchAdReportsData = async (dates, checkCacheFirst, customSetLoading, silent, splitIncrement, { range, breakdowns, providerAccountIdOverride, integrationIdOverride } = {}) => {
        // Used inside catch + finally — declared at top so it's always in scope.
        let key
        let slotAcquired = false
        // True only if THIS call marked the key in-flight. A deduped caller (which
        // awaits another caller's in-flight fetch) leaves this false so the finally
        // block won't unmark a key it doesn't own.
        let didMarkInFlight = false
        try {
            // providerAccountIdOverride lets multi-account view-widget callers fetch
            // a specific account that isn't the user's currently-selected one.
            const targetAccountId = providerAccountIdOverride || chosenAdAccount
            // integrationIdOverride routes the request through a specific integration
            // (e.g. a TikTok-owned ad account) instead of the user's currently-selected one.
            // Without this, picking a TikTok account in a widget would call the Meta endpoint.
            const targetIntegration = integrationIdOverride
                ? allIntegrations.find(i => i._id === integrationIdOverride)
                : relevantIntegration
            if (!targetIntegration || !targetAccountId || targetIntegration.reAuthRequire) {
                return
            }

            const sinceDate = generateDateKey(dates.from)
            const untilDate = generateDateKey(dates.to)

            // Axon asset reports use a range-based cache key, creative_set reports append the breakdown to the key
            key = `${targetAccountId}_${sinceDate}_${untilDate}`
            if (range) {
                key = `${targetAccountId}_range_${range}`
            }
            if (breakdowns) {
                key = `${key}_${breakdowns}`
            }
            // Reset any prior error for this key — we're attempting a fresh fetch.
            clearAdsKeyError && clearAdsKeyError(key)
            const keyWithIncrement = splitIncrement ? `${key}_split_${splitIncrement}` : key

            if (checkCacheFirst) {
                const existingData = getAdsByKey(key)
                const existingDataWithIncrement = getAdsByKey(keyWithIncrement)
                if (existingData && existingDataWithIncrement) {
                    return
                }
            }

            // Dedupe simultaneous callers fetching the same ads dataset (multiple
            // view widgets sharing the same scope on first render, or the home-page
            // prefetch followed by CreativePage's own fetch). We don't fire a
            // duplicate request — but we DO surface this caller's loading state and
            // await the in-flight fetch, so the page shows a loader instead of an
            // empty state until the originator's data lands and re-renders all
            // consumers. We didn't mark the key, so the finally block must not
            // unmark it (didMarkInFlight stays false) — that's the originator's job.
            if (isAdsKeyInFlight && isAdsKeyInFlight(key)) {
                customSetLoading ? customSetLoading(true) : setIsFetchingData(true)
                const inFlightPromise = getAdsKeyInFlightPromise && getAdsKeyInFlightPromise(key)
                if (inFlightPromise) {
                    await inFlightPromise
                }
                return
            }
            markAdsKeyInFlight && markAdsKeyInFlight(key)
            didMarkInFlight = true

            customSetLoading ? customSetLoading(true) : setIsFetchingData(true)

            let data = []
            let headers = {}

            if (isDemoSpace) {
                data = syncAdsDataToUse
            } else {
                // Wait for a slot before hitting the network. The user already
                // sees "loading" via customSetLoading above, so queuing here
                // just paces requests without changing UX.
                await acquireFetchSlot()
                slotAcquired = true
                const requestPayload = {
                    providerAccountId: targetAccountId,
                    sinceDate,
                    untilDate,
                    ignoreCache: !checkCacheFirst,
                    daysToSplitIncrement: splitIncrement,
                }
                // Axon asset reports use a fixed range instead of date params
                if (range) {
                    requestPayload.range = range
                }
                // Axon creative_set reports require a breakdowns param
                if (breakdowns) {
                    requestPayload.breakdowns = breakdowns
                }
                const response = await API.generateUnifiedAdsReport(targetIntegration._id, requestPayload)

                data = response.data
                headers = response.headers

                // In case the report is too large, we need to fetch it from the signed url after the backend upload it to the bucket
                if (data?.isLargeReport && data?.signedUrl) {
                    const reportResponse = await fetch(data.signedUrl)
                    const reportData = await reportResponse.json()
                    data = reportData
                }

                // Stamp every ad with its source account + provider so multi-account
                // consumers (charts, cards, badges) can show where each row came from.
                // Group rows preserve `ads: [ad, ...]`, so this stamp surfaces through.
                if (Array.isArray(data)) {
                    for (const ad of data) {
                        if (ad && typeof ad === 'object') {
                            ad._accountId = targetAccountId
                            ad._provider = targetIntegration.provider
                        }
                    }
                }
            }


            if (!data.length) {
                if (!silent) {
                    toast.info('No data found for the selected date range')
                }
                updateAdsByKey(key, [])
                updateAdsByKey(keyWithIncrement, [])
                return
            }

            // Ad spend cap is disabled for now (also commented out server-side in
            // integrations/utils/limits.js), so don't surface the cap banner even
            // if a stale backend still sends the header.
            // if (headers['x-ad-spend-cap-limit'] === 'apply') {
            //     toggleAppBanner("ad-spend-limit")
            // }


            // Naming convention relevant to the ads
            const extractedNamingConventionValues = extractNamingConventionValues(namingConvention, data)
            updateNamingConventionValuesBySpaceId(space._id, extractedNamingConventionValues)

            // Extract campaigns relevant to the ads
            const campaignsRelevantToAds = extractAllCampaignsFromAdReport(data)
            updateCampaignsRelevantToAdsBySpaceId(space._id, campaignsRelevantToAds)

            // Extract ad sets relevant to the ads
            const adSetsRelevantToAds = extractAllAdSetsFromAdReport(data)
            updateAdSetsRelevantToAdsBySpaceId(space._id, adSetsRelevantToAds)

            // We extract the relevant creative ids of those ads in this report, this way we fetch only what we need
            const creativeIds = extractAllCreativesIdsFromAdReport(data)



            // TODO - this needs attentions, trigger too many times and something the images not found after re-fetch when done analyzing.
            // Trigger the analyze process polling to check when the server is done analyzing the creatives - TODO, this is shit, and it's trigger many times.
            triggerAnalyzeProcessPolling(creativeIds)

            // const creativesResponse = await API.getCreatives(chosenAdAccount)

            // Get the creatives and set all into state.
            let creativesResponse = []
            if (isDemoSpace) {
                creativesResponse = { data: syncCreativeDataToUse }
            } else {
                creativesResponse = await API.getManyCreativesById(creativeIds)
            }

            updateCreativeState(creativesResponse.data)
            updateAdsByKey(key, data)
            updateAdsByKey(keyWithIncrement, data)

        } catch (e) {
            // These are invalid access errors we can get from Meta, currently we handle it here by marking the integration as 'reAuthRequire' so we ask the user to re authenticate with meta
            const isTokenExpiredError = e?.message?.toLowerCase()?.includes('session has expired') // "Error validating access token: Session has expired on Thursday, 09-Jan-25 08:07:54 PST. The current time is Tuesday, 11-Feb-25 02:34:41 PST."
            const isInvalidSession = e?.message?.toLowerCase()?.includes('session has been invalidated') // "Error validating access token: Session has been invalidated because the user changed their password or Facebook has changed the session for security reasons."
            if (isTokenExpiredError || isInvalidSession) {
                setMetaConnectionAsInActive(relevantIntegration._id)
                return
            }

            handleError(e)
            if (!silent) {
                toast.error(e.message)
            }
            console.log(e)
            // Record the error so consumers (view widgets) can render an error state
            // instead of looping on the loading skeleton.
            if (key && setAdsKeyError) {
                setAdsKeyError(key, e?.message || 'Failed to load report')
            }
        } finally {
            customSetLoading ? customSetLoading(false) : setIsFetchingData(false)
            // Only the caller that marked this key in-flight may clear it. A deduped
            // caller (didMarkInFlight === false) must NOT unmark — doing so would
            // resolve the originator's promise early and let a third caller fire a
            // duplicate request while the real fetch is still running.
            if (didMarkInFlight) {
                unmarkAdsKeyInFlight && unmarkAdsKeyInFlight(key)
            }
            if (slotAcquired) {
                releaseFetchSlot()
            }
        }
    }

    // Generate the grouping report to present on the UI
    // Axon reports pass 'range' or 'breakdowns' to match the cache key used during fetch
    // cacheKeyOverride lets multi-account callers store the report under a key that
    // identifies the account UNION rather than just the global chosen account.
    const generateReportByGroupType = (adsData, chosenFilters, dates, internalCustomMetrics, platformOverride, { range, breakdowns, cacheKeyOverride } = {}) => {
        const platform = platformOverride || selectedAdAccount?.provider || integrationProvider || 'meta'

        const sinceDate = generateDateKey(dates.from)
        const untilDate = generateDateKey(dates.to)

        // Cache key must match the key used in fetchAdReportsData
        let key = `${chosenAdAccount}_${sinceDate}_${untilDate}`
        if (range) {
            key = `${chosenAdAccount}_range_${range}`
        }
        if (breakdowns) {
            key = `${key}_${breakdowns}`
        }
        if (cacheKeyOverride) {
            key = cacheKeyOverride
        }
        const filteredAdsData = filterAdsData(chosenFilters, adsData, 'ad_level', getCreativeFromState)
        // We calculate the benchmark before we calcualte the report cause some metric needs to be calcualted from the benchmark, for example the 'spend_percentage' metric
        const benchmark = generateRawAdsBenchmarkReport(filteredAdsData, attributionWindowKeys, platform)
        const benchmarkByDate = generateRawAdsBenchmarkReportByDate(filteredAdsData, attributionWindowKeys, platform)

        // We need this anyway since it used on the CreativeInsight dialog which could be accessed all typ of reports
        const adGroups = generateAdGroupsReports(filteredAdsData, attributionWindowKeys, benchmark, internalCustomMetrics, platform)
        const reportByCreativeGroupAfterFilters = filterAdsData(chosenFilters, adGroups.adGroupByCreative, 'ad_group_level', getCreativeFromState)
        const reportsByGroupType = {
            ad_name: filterAdsData(chosenFilters, adGroups.adGroupByName, 'ad_group_level', getCreativeFromState),
            ad_copy: filterAdsData(chosenFilters, adGroups.adGroupByCopy, 'ad_group_level', getCreativeFromState),
            ad_headline: filterAdsData(chosenFilters, adGroups.adGroupByHeadline, 'ad_group_level', getCreativeFromState),
            ad_landing_page: filterAdsData(chosenFilters, adGroups.adGroupByLandingPage, 'ad_group_level', getCreativeFromState),
            ad_unique: filterAdsData(chosenFilters, adGroups.adGroupsByAdUnique, 'ad_group_level', getCreativeFromState),
            ad_creative: reportByCreativeGroupAfterFilters,
            ad_creative_benchmark: benchmark,
            ad_creative_benchmark_by_date: benchmarkByDate
        }

        // One slot per naming-convention property so switching properties needs no recompute
        const namingConventionProperties = namingConvention?.properties || []
        namingConventionProperties.forEach(property => {
            const groups = generateNamingConventionReport(filteredAdsData, namingConvention, property.name, attributionWindowKeys, benchmark, internalCustomMetrics, platform)
            const slotKey = groupByCacheKey({ key: NAMING_CONVENTION_GROUP_KEY, property: property.name })
            reportsByGroupType[slotKey] = filterAdsData(chosenFilters, groups, 'ad_group_level', getCreativeFromState)
        })

        updateReportByGroupType(key, reportsByGroupType)

        return { filteredAdsData, reportsByGroupType }
    }

    // Build the asset-breakdown group report from an already-fetched broken-down
    // dataset and store it into the base report key's group-type slot. Mirrors
    // generateReportByGroupType's filter/benchmark flow but for a single asset dim.
    const buildAssetBreakdownReport = (brokenDownAdsData, groupType, chosenFilters, dates, internalCustomMetrics, platformOverride) => {
        const platform = platformOverride || selectedAdAccount?.provider || integrationProvider || 'meta'

        const sinceDate = generateDateKey(dates.from)
        const untilDate = generateDateKey(dates.to)
        const baseKey = `${chosenAdAccount}_${sinceDate}_${untilDate}`

        const filteredAdsData = filterAdsData(chosenFilters, brokenDownAdsData, 'ad_level', getCreativeFromState)
        const benchmark = generateRawAdsBenchmarkReport(filteredAdsData, attributionWindowKeys, platform)

        let groups = generateAssetBreakdownReport(filteredAdsData, groupType, attributionWindowKeys, benchmark, internalCustomMetrics, platform)
        groups = filterAdsData(chosenFilters, groups, 'ad_group_level', getCreativeFromState)

        updateReportGroupSlot(baseKey, groupType, groups)
        return groups
    }


    return {
        fetchAdReportsData,
        generateReportByGroupType,
        buildAssetBreakdownReport
    }
}